import React, { useEffect, useState } from 'react';
import { cx, prefersReducedMotion } from '../ui';
import { intelligence } from '../content';
import { PERSONA, PHOTO, UNIT, type Persona } from './data';
import { Hot, Icon, KD, HalvardMark, Photo, RatingPlate, Signature, StatusBar, UnitPhoto } from './parts';

type P = { step: number; acting: boolean; hot: boolean; onAct: () => void; hotLabel: string };

const card = { boxShadow: `inset 0 0 0 1px ${KD.line}` };

function PersonaPill({ persona }: { persona: Persona }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-[3px] text-[10px] font-semibold" style={{ color: KD.ink, ...card }}>
      {persona === 'installer' ? <Icon.wrench size={11} /> : persona === 'public' ? <Icon.doc size={11} /> : <Icon.home size={11} />}
      {PERSONA[persona].role}
    </span>
  );
}

function BrandHeader({ persona }: { persona: Persona }) {
  return (
    <div className="flex items-center justify-between px-4 pb-2.5">
      <HalvardMark size={17} />
      <PersonaPill persona={persona} />
    </div>
  );
}

function SubHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="flex items-center gap-2 border-b px-4 pb-2.5" style={{ borderColor: KD.line }}>
      <span className="grid h-7 w-7 place-items-center rounded-full bg-white" style={{ color: KD.ink, ...card, transform: 'scaleX(-1)' }}>
        <Icon.chev size={14} />
      </span>
      <div className="min-w-0 leading-tight">
        <div className="text-[13px] font-bold" style={{ color: KD.ink }}>{title}</div>
        {sub && <div className="font-mono text-[10px]" style={{ color: KD.muted }}>{sub}</div>}
      </div>
    </div>
  );
}

// The only trace of BrandedMark on the maker's pages: a whisper at the very bottom.
function Powered() {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-[7px] text-center text-[8px] font-medium tracking-wide" style={{ color: '#B7BDBA' }}>
      Powered by BrandedMark
    </div>
  );
}

function Btn({ children, light = false, className }: { children: React.ReactNode; light?: boolean; className?: string }) {
  return (
    <div
      className={cx('flex h-[42px] items-center justify-center gap-2 rounded-[12px] text-[13px] font-semibold', className)}
      style={light ? { background: '#fff', color: KD.ink, ...card } : { background: KD.accent, color: '#fff' }}
    >
      {children}
    </div>
  );
}

const Tick = ({ on = true }: { on?: boolean }) => (
  <span className="grid h-[16px] w-[16px] shrink-0 place-items-center rounded-[5px]" style={on ? { background: KD.accent, color: '#fff' } : { boxShadow: `inset 0 0 0 1.5px ${KD.line}`, background: '#fff' }}>
    {on && <Icon.check size={11} />}
  </span>
);

function Rows({ rows, className }: { rows: [string, string][]; className?: string }) {
  return (
    <dl className={cx('divide-y rounded-[14px] bg-white text-[11.5px]', className)} style={{ ...card, borderColor: KD.line }}>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 px-3 py-2.5" style={{ borderColor: KD.line }}>
          <dt className="shrink-0" style={{ color: KD.muted }}>{k}</dt>
          <dd key={v} className="bmt-flash text-right font-semibold" style={{ color: KD.ink }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------- 1. Camera on the rating plate ----------------
function Scan({ acting, hot, onAct, hotLabel }: P) {
  return (
    <div className="absolute inset-0" style={{ background: '#0E1113' }}>
      <div className="absolute inset-0 overflow-hidden">
        <Photo
          src={PHOTO.install}
          fallback={PHOTO.install}
          alt=""
          eager
          className="h-full w-full scale-[1.9]"
          style={{ objectPosition: '100% 55%', transformOrigin: '88% 58%', filter: 'brightness(.62) saturate(.9)' }}
        />
      </div>
      <div className="relative z-10">
        <StatusBar dark />
      </div>
      <div className="absolute inset-x-0 top-[58px] z-10 flex justify-center">
        <span className="whitespace-nowrap rounded-full bg-black/55 px-3 py-1 text-[11px] font-medium text-white">
          Photo · Video · <b>Scan</b>
        </span>
      </div>
      <div className="absolute inset-x-0 top-[42%] z-10 flex -translate-y-1/2 justify-center">
        <Hot active={hot} label={hotLabel} onAct={onAct} radius={14} tip="bottom">
          <div className="relative p-3">
            {['left-0 top-0 border-l-[3px] border-t-[3px] rounded-tl-[10px]', 'right-0 top-0 border-r-[3px] border-t-[3px] rounded-tr-[10px]', 'left-0 bottom-0 border-l-[3px] border-b-[3px] rounded-bl-[10px]', 'right-0 bottom-0 border-r-[3px] border-b-[3px] rounded-br-[10px]'].map((c) => (
              <span key={c} className={cx('absolute h-5 w-5 transition-colors duration-200', c)} style={{ borderColor: acting ? '#8FD53A' : 'rgba(255,255,255,.9)' }} />
            ))}
            <RatingPlate qr={56} className="w-[214px]" />
            {acting && <span className="bmt-scanline" aria-hidden="true" />}
          </div>
        </Hot>
      </div>
      <div className={cx('absolute inset-x-4 bottom-[96px] z-10 transition-all duration-200', acting ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0')}>
        <div className="flex items-center gap-2 rounded-[14px] bg-white/95 px-3 py-2.5 text-[11.5px]" style={{ color: KD.ink }}>
          <span className="grid h-7 w-7 place-items-center rounded-[8px]" style={{ background: KD.accent, color: '#fff' }}><Icon.link size={14} /></span>
          <span className="min-w-0 leading-tight">
            <b className="block">Open your Halvard AW-9</b>
            <span className="font-mono text-[10px] opacity-70">{UNIT.domain}</span>
          </span>
        </div>
      </div>
      <div className="absolute bottom-6 left-1/2 z-10 h-[54px] w-[54px] -translate-x-1/2 rounded-full border-[3px] border-white/90 p-[3px]">
        <div className="h-full w-full rounded-full bg-white/90" />
      </div>
    </div>
  );
}

// ---------------- 2. Commissioning ----------------
function Commission({ acting, hot, onAct, hotLabel }: P) {
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <BrandHeader persona="installer" />
      <div className="mx-4 flex items-center gap-3 rounded-[14px] bg-white p-2" style={card}>
        <UnitPhoto className="h-[46px] w-[58px] shrink-0 rounded-[9px]" />
        <div className="min-w-0 leading-tight">
          <div className="text-[12.5px] font-bold" style={{ color: KD.ink }}>Halvard AW-9</div>
          <div className="font-mono text-[10px]" style={{ color: KD.muted }}>{UNIT.serial}</div>
          <div className="mt-0.5 text-[10px]" style={{ color: KD.muted }}>14 Orchard Lane</div>
        </div>
      </div>
      <div className="mt-3 flex items-baseline justify-between px-4">
        <div className="text-[12px] font-bold" style={{ color: KD.ink }}>Commissioning checklist</div>
        <div className="text-[10px] font-semibold" style={{ color: KD.accent }}>4 of 4 done</div>
      </div>
      <ul className="mt-1.5 space-y-[7px] px-4">
        {UNIT.checks.map((c) => (
          <li key={c} className="flex items-center gap-2 text-[11.5px]" style={{ color: KD.ink }}>
            <Tick />
            {c}
          </li>
        ))}
      </ul>
      <div className="mx-4 mt-3 grid grid-cols-2 gap-2 text-[10.5px]">
        {[
          ['Flow temperature', '45°C'],
          ['Hot water', '50°C'],
        ].map(([k, v]) => (
          <div key={k} className="rounded-[10px] bg-white px-2.5 py-1.5 leading-tight" style={card}>
            <span className="block" style={{ color: KD.muted }}>{k}</span>
            <b style={{ color: KD.ink }}>{v}</b>
          </div>
        ))}
      </div>
      <div className="mx-4 mt-2.5 grid grid-cols-2 gap-2">
        {[
          { who: 'Brightwell Heating', signed: true, v: 0 as const },
          { who: 'Homeowner', signed: acting, v: 1 as const },
        ].map((s) => (
          <div key={s.who} className="rounded-[12px] bg-white px-2.5 pb-1.5 pt-1" style={card}>
            <div className="h-[34px]">{s.signed ? <Signature variant={s.v} draw={s.v === 1} /> : <div className="pt-3 text-center text-[10px]" style={{ color: '#A8AEAB' }}>Sign here</div>}</div>
            <div className="border-t pt-1 text-[9.5px] font-medium" style={{ borderColor: KD.line, color: KD.muted }}>{s.who}</div>
          </div>
        ))}
      </div>
      <div className="mt-auto px-4 pb-8">
        <Hot active={hot} label={hotLabel} onAct={onAct} tip="none">
          <Btn>{acting ? 'Sealing the record…' : 'Sign and commission'}</Btn>
        </Hot>
      </div>
      <Powered />
    </div>
  );
}

// ---------------- 3. Installer: done ----------------
function Commissioned() {
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <BrandHeader persona="installer" />
      <div className="px-4 pt-4">
        <span className="grid h-12 w-12 place-items-center rounded-full text-white" style={{ background: KD.accent }}>
          <Icon.check size={26} />
        </span>
        <div className="mt-3 text-[21px] font-extrabold leading-[1.1] tracking-[-0.02em]" style={{ color: KD.ink }}>
          Commissioned.
          <br />
          Warranty started.
        </div>
        <div className="mt-1.5 font-mono text-[10.5px]" style={{ color: KD.muted }}>{UNIT.serial}</div>
      </div>
      <Rows
        className="mx-4 mt-4"
        rows={[
          ['Warranty', `Active from ${UNIT.handover}`],
          ['Cover', `Until ${UNIT.warrantyEnd}`],
          ['Registered to', '14 Orchard Lane'],
          ['Installer', 'Brightwell Heating'],
          ['Record', 'Sealed PDF, signed'],
        ]}
      />
      <div className="mx-4 mt-3 flex items-start gap-2 text-[10.5px]" style={{ color: KD.muted }}>
        <Icon.mail size={13} />
        <span>Copies sent to Brightwell Heating and the homeowner.</span>
      </div>
      <div className="mt-auto px-4 pb-8">
        <Btn light>Next job</Btn>
      </div>
      <Powered />
    </div>
  );
}

// ---------------- 4 / 8. Owner home ----------------
function MenuRow({ icon, t, d }: { icon: React.ReactNode; t: string; d: string }) {
  return (
    <div className="flex items-center gap-3 px-3 py-[8px]">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px]" style={{ background: KD.soft, color: KD.accent }}>{icon}</span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block text-[12.5px] font-semibold" style={{ color: KD.ink }}>{t}</span>
        <span className="block truncate text-[10.5px]" style={{ color: KD.muted }}>{d}</span>
      </span>
      <span style={{ color: '#AEB6B3' }}><Icon.chev size={14} /></span>
    </div>
  );
}

function OwnerHome({ step, acting, hot, onAct, hotLabel }: P) {
  const caseView = step === 8;
  const bulletin = caseView && acting;
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <BrandHeader persona="owner" />
      {bulletin && (
        <div className="bmt-notify absolute inset-x-2.5 top-[44px] z-20 rounded-[18px] bg-white/95 p-3 text-[11px] shadow-[0_12px_30px_-10px_rgba(0,0,0,.35)]" style={{ color: KD.ink }}>
          <div className="flex items-center gap-1.5 text-[9.5px] font-semibold" style={{ color: KD.muted }}>
            <HalvardMark size={12} /> <span className="ml-auto">now</span>
          </div>
          <b className="mt-1 block text-[12px]">A note about your Halvard AW-9</b>
          Brightwell Heating will check the fan connector at your next visit. Nothing for you to do.
        </div>
      )}
      <div className="px-4">
        {caseView ? (
          <>
            {bulletin && (
              <div className="mb-2 flex gap-2 rounded-[12px] p-2.5 text-[10.5px] leading-snug" style={{ background: KD.soft, boxShadow: `inset 0 0 0 1.5px ${KD.accent}`, color: KD.ink }}>
                <Icon.bell size={15} />
                <span>
                  <b className="block text-[11.5px]">Service bulletin</b>Fan connector check, done at your next service.
                </span>
              </div>
            )}
            <div className="rounded-[14px] bg-white p-3" style={card}>
              <div className="flex items-center justify-between text-[10px] font-semibold" style={{ color: KD.muted }}>
                <span>Case {UNIT.caseNo}</span>
                <span className="rounded-full px-2 py-0.5" style={{ background: KD.soft, color: KD.accent }}>With Brightwell Heating</span>
              </div>
              <div className="mt-1 text-[13px] font-bold" style={{ color: KD.ink }}>E21, fan stopped</div>
              <div className="mt-0.5 text-[10.5px]" style={{ color: KD.muted }}>Covered by warranty · Visit booked for Thursday</div>
            </div>
          </>
        ) : (
          <div className="overflow-hidden rounded-[16px] bg-white" style={card}>
            <div className="relative h-[104px]">
              <UnitPhoto className="h-full w-full" alt="Your Halvard AW-9 heat pump" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(21,48,45,.82) 0%, rgba(21,48,45,.35) 58%, rgba(21,48,45,0) 100%)' }} />
              <div className="absolute bottom-2.5 left-3 leading-tight text-white">
                <div className="text-[10px] font-medium text-white/80">Meet your</div>
                <div className="text-[19px] font-extrabold tracking-[-0.02em]">Halvard AW-9</div>
                <div className="font-mono text-[9px] text-white/75">{UNIT.serial}</div>
              </div>
            </div>
            <div className="flex items-center justify-between px-3 py-2 text-[10.5px]" style={{ color: KD.ink }}>
              <span className="flex items-center gap-1.5 font-semibold"><span style={{ color: KD.accent }}><Icon.shield size={14} /></span>Warranty active</span>
              <span style={{ color: KD.muted }}>Until Mar 2028</span>
            </div>
          </div>
        )}
        {!caseView && (
          <div className="mt-2 rounded-[14px] p-2.5 text-[10.5px] leading-snug" style={{ background: KD.soft, color: KD.ink }}>
            <b className="flex items-center gap-1.5 text-[11px]" style={{ color: KD.accent }}><Icon.thermo size={13} />Tip for your radiators</b>
            <span className="mt-0.5 block">Set the flow temperature to 45°C and leave it running steadily.</span>
          </div>
        )}
      </div>
      <div className="mx-4 mt-2 divide-y rounded-[14px] bg-white" style={{ ...card, borderColor: KD.line }}>
        <MenuRow icon={<Icon.book size={16} />} t="Setup guide" d="For your AW-9 with radiators" />
        <Hot active={hot && !caseView} label={hotLabel} onAct={onAct} radius={10} tip="top">
          <MenuRow icon={<Icon.chat size={16} />} t="Ask a question" d="Answers from Halvard’s manuals" />
        </Hot>
        <MenuRow icon={<Icon.calendar size={16} />} t="Book a service" d="With Brightwell Heating" />
        {!caseView && <MenuRow icon={<Icon.alert size={16} />} t="Report a problem" d="Goes to your installer, with the details" />}
      </div>
      <Powered />
    </div>
  );
}

// ---------------- 5. Grounded chat ----------------
function Chat({ hot, onAct, hotLabel }: P) {
  const [typed, setTyped] = useState(() => prefersReducedMotion());
  useEffect(() => {
    if (typed) return;
    const t = window.setTimeout(() => setTyped(true), 800);
    return () => window.clearTimeout(t);
  }, [typed]);
  const s = intelligence.sample;
  const [src, cover] = s.source.split(' · ');
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <SubHeader title="Ask about your AW-9" sub={UNIT.serial} />
      <div className="flex-1 space-y-2.5 px-3 pt-3">
        <div className="ml-auto max-w-[82%] rounded-[16px] rounded-br-[5px] px-3 py-2 text-[11.5px] leading-snug text-white" style={{ background: KD.accent }}>
          {s.q}
        </div>
        {!typed && (
          <div className="bmt-typing flex w-[54px] gap-1 rounded-[14px] bg-white px-3 py-2.5" style={card} aria-hidden="true">
            <i /><i /><i />
          </div>
        )}
        {typed && (
          <>
            <div className="bmt-rowin max-w-[94%] rounded-[16px] rounded-bl-[5px] bg-white px-3 py-2.5 text-[11.5px] leading-snug" style={{ ...card, color: KD.ink }}>
              {s.a}
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="inline-flex items-center gap-1 rounded-[7px] px-1.5 py-1 text-[9.5px] font-semibold" style={{ background: KD.soft, color: KD.accent }}>
                  <Icon.doc size={11} />
                  {src}
                </span>
                {cover && (
                  <span className="inline-flex items-center gap-1 rounded-[7px] px-1.5 py-1 text-[9.5px] font-semibold" style={{ background: KD.paper, color: KD.muted }}>
                    <Icon.shield size={11} />
                    {cover}
                  </span>
                )}
              </div>
            </div>
            <div className="bmt-rowin space-y-2" style={{ animationDelay: '.25s' }}>
              <Hot active={hot} label={hotLabel} onAct={onAct} radius={14} tip="bottom">
                <div className="flex items-center gap-2.5 rounded-[14px] bg-white p-2" style={{ boxShadow: `inset 0 0 0 1.5px ${KD.accent}` }}>
                  <span className="grid h-8 w-8 place-items-center rounded-[10px]" style={{ background: KD.soft, color: KD.accent }}><Icon.fan size={17} /></span>
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block text-[11.5px] font-bold" style={{ color: KD.ink }}>Fan motor for this serial</span>
                    <span className="block text-[10px]" style={{ color: KD.muted }}>Covered by your warranty</span>
                  </span>
                  <span style={{ color: KD.accent }}><Icon.chev size={14} /></span>
                </div>
              </Hot>
            </div>
          </>
        )}
      </div>
      <div className="px-3 pb-8">
        <div className="mb-2 flex items-center justify-center gap-1.5 text-[11px] font-semibold" style={{ color: KD.accent }}>
          <Icon.user size={13} /> Talk to a person
        </div>
        <div className="flex h-[38px] items-center rounded-full bg-white px-4 text-[11.5px]" style={{ ...card, color: '#A8AEAB' }}>
          Ask anything about your heat pump
        </div>
      </div>
      <Powered />
    </div>
  );
}

// ---------------- 6. Parts ----------------
const PARTS = [
  { n: 'Fan motor assembly', p: 'KD-FM-0920', price: 'Covered', sel: true, icon: <Icon.fan size={18} /> },
  { n: 'Fan guard', p: 'KD-FG-0310', price: '£24.00', icon: <Icon.cog size={18} /> },
  { n: 'System filter cartridge', p: 'KD-SF-0150', price: '£18.50', icon: <Icon.wrench size={18} /> },
];
function Parts({ acting, hot, onAct, hotLabel }: P) {
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <SubHeader title="Parts for your AW-9" sub={UNIT.serial} />
      <div className="px-4 pt-3">
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold" style={{ background: KD.soft, color: KD.accent }}>
          <Icon.check size={11} /> Only parts that fit this serial
        </span>
      </div>
      <ul className="mt-2.5 space-y-2 px-4">
        {PARTS.map((p) => (
          <li key={p.p} className="flex items-center gap-2.5 rounded-[14px] bg-white p-2.5" style={{ boxShadow: `inset 0 0 0 ${p.sel ? 1.5 : 1}px ${p.sel ? KD.accent : KD.line}` }}>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px]" style={{ background: KD.soft, color: KD.accent }}>{p.icon}</span>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-[12px] font-semibold" style={{ color: KD.ink }}>{p.n}</span>
              <span className="block font-mono text-[9.5px]" style={{ color: KD.muted }}>{p.p}</span>
              {p.sel && <span className="mt-0.5 block text-[10px] font-semibold" style={{ color: KD.accent }}>Fixes the E21 fault</span>}
            </span>
            <span className={cx('text-right font-bold', p.sel ? 'self-start rounded-full px-1.5 py-0.5 text-[9.5px]' : 'text-[12px]')} style={p.sel ? { color: KD.accent, background: KD.soft } : { color: KD.ink }}>{p.price}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto rounded-t-[22px] bg-white px-4 pb-8 pt-3.5 shadow-[0_-10px_30px_-18px_rgba(0,0,0,.3)]">
        <div className="flex items-center justify-between gap-3 text-[11px]" style={{ color: KD.ink }}>
          <span className="leading-tight">
            <b className="block text-[12px]">Covered until Mar 2028</b>
            <span style={{ color: KD.muted }}>Brightwell Heating fits it</span>
          </span>
          <b className="text-[14px]">£0.00</b>
        </div>
        <div className="mt-2.5">
          <Hot active={hot} label={hotLabel} onAct={onAct} tip="none">
            <Btn>
              {acting ? (
                <>
                  <Icon.check size={15} /> Added to your claim
                </>
              ) : (
                'Claim under warranty'
              )}
            </Btn>
          </Hot>
        </div>
      </div>
      <Powered />
    </div>
  );
}

// ---------------- 7. Report a problem ----------------
function Shot({ pos, zoom = 2.2 }: { pos: string; zoom?: number }) {
  return (
    <span className="relative block h-[58px] w-[58px] overflow-hidden rounded-[10px]">
      <Photo src="/images/v2/gen/heatpump-commission-b-thumb.jpg" fallback="/images/v2/gen/heatpump-commission-b-thumb.jpg" alt="" className="h-full w-full" style={{ objectPosition: pos, transform: `scale(${zoom})`, transformOrigin: pos }} />
    </span>
  );
}
function Report({ acting, hot, onAct, hotLabel }: P) {
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <SubHeader title="Report a problem" sub={UNIT.serial} />
      <div className="px-4 pt-3">
        <div className="text-[11.5px] font-bold" style={{ color: KD.ink }}>What’s happening?</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {['E21, fan stopped', 'No heating', 'Noise', 'Something else'].map((c, i) => (
            <span key={c} className="rounded-full px-2.5 py-1 text-[10.5px] font-semibold" style={i === 0 ? { background: KD.accent, color: '#fff' } : { background: '#fff', color: KD.ink, ...card }}>
              {c}
            </span>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Shot pos="96% 55%" />
          <Shot pos="78% 40%" zoom={2.6} />
          <span className="grid h-[58px] w-[58px] place-items-center rounded-[10px] bg-white" style={{ ...card, color: KD.muted }}><Icon.camera size={18} /></span>
        </div>
        <div className="mt-2.5 rounded-[12px] bg-white px-3 py-2 text-[11px] leading-snug" style={{ ...card, color: KD.ink }}>
          E21 came back this morning. Defrost cleared it for an hour, then the fan stopped again.
        </div>
        <div className="mt-2.5 rounded-[12px] p-2.5" style={{ background: KD.soft }}>
          <div className="text-[9.5px] font-semibold uppercase tracking-wider" style={{ color: KD.muted }}>Sent with your report</div>
          <ul className="mt-1.5 space-y-1 text-[10.5px]" style={{ color: KD.ink }}>
            {[`Serial ${UNIT.serial}`, 'Warranty active until Mar 2028', 'Commissioning record and history', 'Part: fan motor assembly', 'Your installer: Brightwell Heating'].map((t) => (
              <li key={t} className="flex items-center gap-1.5"><span style={{ color: KD.accent }}><Icon.check size={12} /></span>{t}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-auto px-4 pb-8">
        <Hot active={hot} label={hotLabel} onAct={onAct} tip="none">
          <Btn>{acting ? 'Sending…' : 'Send to Brightwell Heating'}</Btn>
        </Hot>
      </div>
      {acting && (
        <div className="bmt-sheet absolute inset-x-0 bottom-0 z-10 rounded-t-[24px] bg-white px-5 pb-10 pt-5 shadow-[0_-20px_40px_-20px_rgba(0,0,0,.35)]">
          <span className="grid h-10 w-10 place-items-center rounded-full text-white" style={{ background: KD.accent }}><Icon.check size={20} /></span>
          <div className="mt-2.5 text-[16px] font-extrabold" style={{ color: KD.ink }}>Case {UNIT.caseNo} opened</div>
          <div className="mt-1 text-[11.5px]" style={{ color: KD.muted }}>Brightwell Heating and Halvard have everything they need. You will hear back today.</div>
        </div>
      )}
      <Powered />
    </div>
  );
}

// ---------------- 9. Sold with the house ----------------
function Transfer({ acting, hot, onAct, hotLabel }: P) {
  if (acting) {
    return (
      <div className="absolute inset-0 flex flex-col">
        <StatusBar />
        <BrandHeader persona="newowner" />
        <div className="px-4">
          <div className="overflow-hidden rounded-[16px]">
            <UnitPhoto className="h-[118px] w-full" alt="Your Halvard AW-9 heat pump" />
          </div>
          <div className="mt-3 text-[10px] font-semibold uppercase tracking-wider" style={{ color: KD.accent }}>Welcome to 14 Orchard Lane</div>
          <div className="mt-1 text-[20px] font-extrabold leading-[1.1] tracking-[-0.02em]" style={{ color: KD.ink }}>Meet your Halvard AW-9.</div>
        </div>
        <Rows
          className="mx-4 mt-3"
          rows={[
            ['History', '7 entries since 2026'],
            ['Setup guide', 'For this unit'],
            ['Installer', 'Brightwell Heating'],
          ]}
        />
        <Powered />
      </div>
    );
  }
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <BrandHeader persona="owner" />
      <div className="px-4 pt-1">
        <div className="text-[19px] font-extrabold leading-[1.1] tracking-[-0.02em]" style={{ color: KD.ink }}>Moving house?</div>
        <div className="mt-1 text-[11px]" style={{ color: KD.muted }}>Pass your heat pump on to the new owners.</div>
        <div className="mt-3 flex items-center gap-2.5 rounded-[14px] bg-white p-2" style={card}>
          <UnitPhoto className="h-[42px] w-[54px] shrink-0 rounded-[9px]" />
          <span className="min-w-0 leading-tight">
            <b className="block text-[12px]" style={{ color: KD.ink }}>Halvard AW-9</b>
            <span className="block font-mono text-[9.5px]" style={{ color: KD.muted }}>{UNIT.serial}</span>
            <span className="block text-[10px]" style={{ color: KD.muted }}>7 entries in its history</span>
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2.5 rounded-[14px] bg-white p-2.5" style={card}>
          <span className="grid h-9 w-9 place-items-center rounded-full text-white" style={{ background: KD.accent }}><Icon.home size={17} /></span>
          <span className="leading-tight">
            <b className="block text-[12.5px]" style={{ color: KD.ink }}>The new owners</b>
            <span className="text-[10px]" style={{ color: KD.muted }}>Accepted your invitation</span>
          </span>
        </div>
        <div className="mt-2.5 grid grid-cols-2 gap-2 text-[10.5px] leading-snug">
          <div className="rounded-[12px] bg-white p-2.5" style={{ ...card, color: KD.ink }}>
            <b className="block text-[11px]">Goes with it</b>
            <span style={{ color: KD.muted }}>Service history, setup guide, any cover left</span>
          </div>
          <div className="rounded-[12px] bg-white p-2.5" style={{ ...card, color: KD.ink }}>
            <b className="block text-[11px]">Stays with you</b>
            <span style={{ color: KD.muted }}>Your name, contact details, messages</span>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 text-[11px]" style={{ color: KD.ink }}>
          <Tick />
          <span>We have sold the house, and the heat pump goes with it.</span>
        </div>
      </div>
      <div className="mt-auto px-4 pb-8">
        <Hot active={hot} label={hotLabel} onAct={onAct} tip="none">
          <Btn>Confirm transfer</Btn>
        </Hot>
      </div>
      <Powered />
    </div>
  );
}

// ---------------- 10. Passport: owner vs recycler ----------------
function Passport({ acting, hot, onAct, hotLabel }: P) {
  const recycler = acting;
  const owner: [string, string][] = [
    ['Manufacturer', 'Halvard Heat Ltd'],
    ['Model', 'AW-9 air source heat pump, 2026'],
    ['Energy', 'Energy label and EPREL entry'],
    ['Repair', 'Spare parts and repair guides'],
  ];
  const restricted: [string, string][] = [
    ['Refrigerant', 'Type, charge, recovery steps'],
    ['Dismantling', 'Guide for this build'],
    ['Materials', 'By component, from the ERP'],
  ];
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <BrandHeader persona="public" />
      <div className="px-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: KD.muted }}>Digital Product Passport</div>
        <div className="mt-0.5 text-[16px] font-extrabold tracking-[-0.01em]" style={{ color: KD.ink }}>Halvard AW-9</div>
        <div className="break-all font-mono text-[9.5px] leading-snug" style={{ color: KD.muted }}>{UNIT.domain}/01/05060012345675/21/{UNIT.serial}</div>
        <div className="mt-2.5 grid grid-cols-2 rounded-full bg-white p-1 text-[11px] font-semibold" style={card}>
          <span className="rounded-full py-1.5 text-center" style={!recycler ? { background: KD.accent, color: '#fff' } : { color: KD.muted }}>Owner</span>
          <Hot active={hot && !recycler} label={hotLabel} onAct={onAct} radius={999} tip="bottom">
            <span className="flex items-center justify-center gap-1 rounded-full py-1.5" style={recycler ? { background: KD.accent, color: '#fff' } : { color: KD.muted }}>
              <Icon.lock size={11} /> Recycler
            </span>
          </Hot>
        </div>
      </div>
      <dl className="mx-4 mt-2.5 divide-y rounded-[14px] bg-white text-[10.5px]" style={{ ...card, borderColor: KD.line }}>
        {owner.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 px-3 py-[7px]" style={{ borderColor: KD.line }}>
            <dt className="shrink-0" style={{ color: KD.muted }}>{k}</dt>
            <dd className="text-right font-semibold" style={{ color: KD.ink }}>{v}</dd>
          </div>
        ))}
        {recycler &&
          restricted.map(([k, v]) => (
            <div key={k} className="bmt-rowin flex justify-between gap-3 px-3 py-[7px]" style={{ borderColor: KD.line, background: '#F6F9F8' }}>
              <dt className="flex shrink-0 items-center gap-1" style={{ color: KD.muted }}><Icon.lock size={10} />{k}</dt>
              <dd className="text-right font-semibold" style={{ color: KD.ink }}>{v}</dd>
            </div>
          ))}
      </dl>
      <div className="mx-4 mt-2 text-[9.5px] leading-snug" style={{ color: KD.muted }}>
        Example fields. Heat pumps have no passport date yet; any passport’s fields will be set by its product group’s rules.
      </div>
      <Powered />
    </div>
  );
}

// ---------------- 11. End of life ----------------
function EndOfLife({ acting, hot, onAct, hotLabel }: P) {
  const materials: [string, string][] = [
    ['Steel casing and frame', 'Recycled'],
    ['Copper pipework and coil', 'Recycled'],
    ['Aluminium fins', 'Recycled'],
    ['Refrigerant', 'Recovered on site'],
    ['Controller board', 'Electronics route'],
  ];
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <BrandHeader persona="newowner" />
      <div className="px-4 pt-1">
        <div className="text-[19px] font-extrabold leading-[1.1] tracking-[-0.02em]" style={{ color: KD.ink }}>The next chapter for your AW-9.</div>
        <div className="mt-1 text-[11px] leading-snug" style={{ color: KD.muted }}>Replacing it? Halvard takes it back.</div>
        <div className="mt-3 rounded-[14px] bg-white p-3" style={{ boxShadow: `inset 0 0 0 1.5px ${KD.accent}` }}>
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-[10px]" style={{ background: KD.soft, color: KD.accent }}><Icon.recycle size={17} /></span>
            <span className="leading-tight">
              <b className="block text-[12px]" style={{ color: KD.ink }}>Halvard take-back</b>
              <span className="text-[10px]" style={{ color: KD.muted }}>Brightwell Heating collects it, free</span>
            </span>
          </div>
        </div>
        <div className="mt-2.5 text-[9.5px] font-semibold uppercase tracking-wider" style={{ color: KD.muted }}>What it’s made of</div>
      </div>
      <dl className="mx-4 mt-1.5 divide-y rounded-[14px] bg-white text-[10.5px]" style={{ ...card, borderColor: KD.line }}>
        {materials.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 px-3 py-[6px]" style={{ borderColor: KD.line }}>
            <dt style={{ color: KD.ink }}>{k}</dt>
            <dd className="text-right" style={{ color: KD.muted }}>{v}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-auto px-4 pb-8">
        <Hot active={hot && !acting} label={hotLabel} onAct={onAct} tip="none">
          <Btn>Book take-back</Btn>
        </Hot>
      </div>
      {acting && (
        <div className="bmt-sheet absolute inset-x-0 bottom-0 z-10 rounded-t-[24px] bg-white px-5 pb-10 pt-5 shadow-[0_-20px_40px_-20px_rgba(0,0,0,.35)]">
          <span className="grid h-10 w-10 place-items-center rounded-full text-white" style={{ background: KD.accent }}><Icon.check size={20} /></span>
          <div className="mt-2.5 text-[16px] font-extrabold" style={{ color: KD.ink }}>Take-back booked</div>
          <div className="mt-1 text-[11.5px] leading-snug" style={{ color: KD.muted }}>Brightwell Heating collects it when the new unit goes in. The materials data goes with it to the recycler.</div>
        </div>
      )}
      <Powered />
    </div>
  );
}

// Persona switch banner: shows briefly whenever the person holding the phone changes.
function SwitchBanner({ persona }: { persona: Persona }) {
  return (
    <div key={persona} className="bmt-switch pointer-events-none absolute inset-x-3 top-[46px] z-20 flex items-center gap-2 rounded-[14px] px-3 py-2 text-[11px] font-semibold text-white" style={{ background: '#241E61' }} role="status">
      <Icon.swap size={14} />
      {PERSONA[persona].switchLine}
    </div>
  );
}

// ---------------- 12. The loop closes: the answer Halvard just added ----------------
function Learned({ acting }: P) {
  return (
    <div className="absolute inset-0 flex flex-col">
      <StatusBar />
      <SubHeader title="Ask about your AW-9" sub={UNIT.serial} />
      <div className="flex-1 space-y-2.5 px-3 pt-3">
        <div className="ml-auto max-w-[82%] rounded-[16px] rounded-br-[5px] px-3 py-2 text-[11.5px] leading-snug text-white" style={{ background: KD.accent }}>
          We are away for two weeks in January. How do I stop it freezing?
        </div>
        {!acting ? (
          <div className="bmt-rowin max-w-[94%] rounded-[16px] rounded-bl-[5px] bg-white px-3 py-2.5 text-[11.5px] leading-snug" style={{ ...card, color: KD.ink }}>
            I could not find that in Halvard&rsquo;s documents for your AW-9, so I have passed your question to Halvard. Brightwell Heating can help in the meantime.
          </div>
        ) : (
          <div className="bmt-rowin max-w-[94%] rounded-[16px] rounded-bl-[5px] bg-white px-3 py-2.5 text-[11.5px] leading-snug" style={{ ...card, color: KD.ink }}>
            Leave it switched on and set holiday mode to 10&deg;C. The AW-9 runs its own frost protection, but only while it has power, so do not switch it off at the isolator.
            <div className="mt-2 flex flex-wrap gap-1">
              <span className="inline-flex items-center gap-1 rounded-[7px] px-1.5 py-1 text-[9.5px] font-semibold" style={{ background: KD.soft, color: KD.accent }}>
                <Icon.doc size={11} />
                Halvard answer · added this week
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="px-3 pb-8">
        <div className="flex h-[38px] items-center rounded-full bg-white px-4 text-[11.5px]" style={{ ...card, color: '#A8AEAB' }}>
          Ask anything about your heat pump
        </div>
      </div>
      <Powered />
    </div>
  );
}

export function PhoneScreen(props: P & { persona: Persona; personaChanged: boolean }) {
  const { step } = props;
  const screen =
    step === 1 ? <Scan {...props} /> :
    step === 2 ? <Commission {...props} /> :
    step === 3 ? <Commissioned /> :
    step === 4 || step === 8 ? <OwnerHome {...props} /> :
    step === 5 ? <Chat {...props} /> :
    step === 6 ? <Parts {...props} /> :
    step === 7 ? <Report {...props} /> :
    step === 9 ? <Transfer {...props} /> :
    step === 10 ? <Passport {...props} /> :
    step === 12 ? <Learned {...props} /> :
    <EndOfLife {...props} />;
  return (
    <>
      <div key={step} className="bmt-screen absolute inset-0">
        {screen}
      </div>
      {props.personaChanged && step !== 1 && step !== 11 && step !== 12 && <SwitchBanner persona={props.persona} />}
      {step === 9 && props.acting && <SwitchBanner persona="newowner" />}
    </>
  );
}
